import { InfoCircleOutlined, SlackOutlined } from "@ant-design/icons";
import { Alert, Card, Spin, Table, Tooltip } from "antd";
import Avatar from "antd/lib/avatar/avatar";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { getWorkspaceId } from "../../lib/queries";
import { getSupaClient } from "../../lib/supabase";
import { setCompaniesData } from "../../redux/actions";
import { DateRangePicker } from "../common";
import { useEndTime, useStartTime } from "../DateContext/DateContext";

const dateFormat = "MM/DD/YY";

const Companies = () => {
  const navigate = useNavigate();
  const supabase = getSupaClient();
  const dispatch = useDispatch();
  const companies = useSelector((state) => state.companies.companies_list);
  const [loading, setLoading] = useState(false);
  const teamId = localStorage.getItem("team-id");
  const [noCompanyView, setNoCompanyView] = useState(false);
  const { startTime } = useStartTime();
  const { endTime } = useEndTime();

  const allColumns = [
    {
      title: "Name",
      dataIndex: "company_name",
      key: "company_name",
      render: (text, record) => (
        <div className="flex flex-row justify-between">
          <div className="flex flex-row justify-start">
            <div>
              <Avatar
                src={`//logo.clearbit.com/${record.company_domain}`}
                style={{ marginRight: 15 }}
              />
            </div>
            <div style={{ maxWidth: "200px" }}>
              <Tooltip title="Click to view the company">
                <div
                  style={{ color: "#1890ff", cursor: "pointer" }}
                  onClick={() => navigate(`/insights/${record.company_id}`)}
                  onKeyDown={() => navigate(`/insights/${record.company_id}`)}
                  tabIndex={0}
                  role="button"
                >
                  {record.company_name}
                </div>
              </Tooltip>
            </div>
          </div>
          <Tooltip title="Click to view the company in new tab">
            <div className="ml-2 pt-1">
              <Link
                to={`/insights/${record.company_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img alt="Link" width="16" src="/external-link.svg" />
              </Link>
            </div>
          </Tooltip>
        </div>
      ),
      width: "25%",
      sorter: (a, b) => a.company_name.localeCompare(b.company_name)
    },
    {
      title: "Total Members",
      dataIndex: "total_members",
      key: "total_members",
      sorter: (a, b) => a.total_members - b.total_members,
      width: "15%",
      render: (text, record) => (
        <div className="flex flex-row justify-around">{text}</div>
      )
    },
    {
      title: "Active Members",
      dataIndex: "active_members",
      key: "active_members",
      render: (text, record) => (
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <span>{record.active_members}</span>
            <div style={{ marginLeft: 5 }}>
              <SlackOutlined />
            </div>
          </div>
        </div>
      ),
      sorter: (a, b) => a.active_members - b.active_members,
      width: "20%"
    },
    {
      title: "Employees",
      dataIndex: "company_size",
      key: "company_size",
      sorter: (a, b) => a.company_size?.length - b.company_size?.length,
      width: "15%"
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "25%"
    }
  ];

  /**
   * Call the companies_overview RPC for the given workspace_id
   * @param {*} workspaceId
   */
  const loadCompanies = async (start, end) => {
    setLoading(true);
    const workspaceId = await getWorkspaceId(teamId);
    if (!workspaceId) {
      setNoCompanyView(true);
    }
    const params = {
      start_time: startTime,
      end_time: endTime,
      slack_team_id: teamId,
      workspace_id: workspaceId
    };
    const { data, error } = await supabase.rpc("companies_overview", params);
    setLoading(false);
    if (error) {
      console.log("error in loadCompanies", error);
    } else {
      dispatch(setCompaniesData(data));
    }
  };

  const loadPageData = async () => {
    if (companies.length === 0) {
      await loadCompanies(startTime, endTime);
    }
  };

  useEffect(() => {
    loadPageData();
  }, []);

  return (
    <>
      {noCompanyView ? (
        <p>Feature not enabled. Please contact support to upgrade your plan</p>
      ) : null}

      <div className="flex justify-end mb-3">
        <div className="mr-5">
          <Tooltip
            title="Based on email and name we identify the company your member works at.
          Members active here are members that have had an activity in the time
          period selected"
          >
            <InfoCircleOutlined style={{ fontSize: "20px", color: "#08c" }} />
          </Tooltip>
        </div>
      </div>

      <Card bordered={false}>
        <div style={{ width: "100%", height: "100%" }}>
          <Spin spinning={loading}>
            <Table
              onChange={(pagination, filters, sorter, extra) => {
                // setTableFiltered(extra.currentDataSource.length);
              }}
              dataSource={companies}
              size="small"
              columns={allColumns}
              rowKey="company_id"
              pagination={{
                defaultPageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: ["50", "100", "200"]
              }}
            />
          </Spin>
        </div>
      </Card>
      {loading || noCompanyView ? null : (
        <div className="mt-20 float-right">
          <a className="text-gray" href="https://clearbit.com">
            Logos provided by Clearbit
          </a>
        </div>
      )}
    </>
  );
};

export default Companies;
